import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, Grid, Stack, Typography } from '@mui/material';
import * as React from 'react';

/**
 * CP-7988: a tag category's schema can carry sections — `x-groups` (`[{name, title, description?, order?}]`)
 * with each property naming its section in `x-group` — so a long property list (Amigo's product_info has 267)
 * reads in parts. Ops author them in the console's schema editor (backend
 * `demo/lib/tag_category_sets/schema_vocabulary.py`, console `features/tags/schema/vocabulary.ts`). They are
 * display only: a tag's `data` stays flat, keyed by property name, whatever section a property sits in.
 */

const UNGROUPED = '';

/** Comparator for a schema's property keys: `x-displayOrder` first, then by key. */
export const byDisplayOrder = (props) => (a, b) => {
    const orderA = props[a]?.['x-displayOrder'];
    const orderB = props[b]?.['x-displayOrder'];
    if (orderA !== undefined && orderB !== undefined) return orderA - orderB;
    if (orderA !== undefined) return -1;
    if (orderB !== undefined) return 1;
    return a.localeCompare(b);
};

/** Every property key of a schema in display order (the flat form). */
export const schemaKeys = (schema) => {
    const props = schema?.properties || {};
    return Object.keys(props)
        .filter((key) => props[key] && typeof props[key] === 'object')
        .sort(byDisplayOrder(props));
};

/**
 * The schema's properties in display order, split into sections the way the console lays them out: declared groups
 * by `order` (declaration order on ties), each with its properties by `x-displayOrder`, then one trailing section
 * (`group: null`) for properties with no `x-group` or one naming an undeclared group (Main Admin's JSON editor does
 * not validate it). Empty groups are left out. `null` when no property sits in a declared group: the form stays flat.
 */
export const schemaSections = (schema) => {
    const props = schema?.properties || {};
    const keys = schemaKeys(schema);
    const groups = (Array.isArray(schema?.['x-groups']) ? schema['x-groups'] : [])
        .filter((g) => g && typeof g === 'object' && typeof g.name === 'string')
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const declared = new Set(groups.map((g) => g.name));
    const byGroup = new Map();
    keys.forEach((key) => {
        const name = props[key]['x-group'];
        const id = typeof name === 'string' && declared.has(name) ? name : UNGROUPED;
        byGroup.set(id, [...(byGroup.get(id) || []), key]);
    });
    if (!groups.some((g) => byGroup.has(g.name))) return null;
    const sections = groups.filter((g) => byGroup.has(g.name)).map((g) => ({ id: g.name, group: g, keys: byGroup.get(g.name) }));
    if (byGroup.has(UNGROUPED)) sections.push({ id: UNGROUPED, group: null, keys: byGroup.get(UNGROUPED) });
    return sections;
};

/**
 * Renders a tag form's schema properties through `renderField(key)` (which returns a `<Grid item>`): flat when the
 * schema declares no sections, else one collapsible panel per section, open by default. A section holding one of
 * `attentionKeys` (required fields left empty) opens again and says so. `stateKey` (the category id) keeps what the
 * viewer collapsed while they move between tags of the same category.
 */
const SchemaSections = ({ schema, renderField, attentionKeys = [], stateKey = '' }) => {
    const sections = React.useMemo(() => schemaSections(schema), [schema]);
    const [collapsed, setCollapsed] = React.useState(() => new Set());
    const sectionKey = (section) => `${stateKey}:${section.id}`;

    const attention = React.useMemo(() => new Set(attentionKeys), [attentionKeys]);
    React.useEffect(() => {
        if (!sections || attention.size === 0) return;
        const reopen = sections.filter((s) => s.keys.some((k) => attention.has(k))).map((s) => `${stateKey}:${s.id}`);
        setCollapsed((prev) => (reopen.some((k) => prev.has(k)) ? new Set([...prev].filter((k) => !reopen.includes(k))) : prev));
    }, [sections, attention, stateKey]);

    if (!sections) return <>{schemaKeys(schema).map(renderField)}</>;

    const allCollapsed = sections.every((s) => collapsed.has(sectionKey(s)));
    const toggle = (section, expanded) => setCollapsed((prev) => {
        const next = new Set(prev);
        if (expanded) next.delete(sectionKey(section));
        else next.add(sectionKey(section));
        return next;
    });
    const setAll = (collapse) => setCollapsed((prev) => {
        const next = new Set(prev);
        sections.forEach((s) => (collapse ? next.add(sectionKey(s)) : next.delete(sectionKey(s))));
        return next;
    });

    return (
        <>
            {sections.length > 2 && (
                <Grid item xs={12} container justifyContent="flex-end" sx={{ pb: 0 }}>
                    <Button size="small" onClick={() => setAll(!allCollapsed)}>
                        {allCollapsed ? 'Expand all sections' : 'Collapse all sections'}
                    </Button>
                </Grid>
            )}
            {sections.map((section) => {
                const expanded = !collapsed.has(sectionKey(section));
                const missing = section.keys.filter((k) => attention.has(k)).length;
                const title = section.group ? section.group.title || section.group.name : 'Other';
                return (
                    <Grid item xs={12} key={`section-${section.id}`}>
                        <Accordion
                            disableGutters
                            variant="outlined"
                            expanded={expanded}
                            onChange={(ev, isExpanded) => toggle(section, isExpanded)}
                            sx={{ borderRadius: 1, '&:before': { display: 'none' } }}
                            data-testid={`tag-section-${section.id || 'other'}`}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack sx={{ minWidth: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="baseline">
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {section.keys.length === 1 ? '1 field' : `${section.keys.length} fields`}
                                        </Typography>
                                    </Stack>
                                    {section.group?.description && (
                                        <Typography variant="body2" color="text.secondary">{section.group.description}</Typography>
                                    )}
                                    {missing > 0 && (
                                        <Typography variant="caption" color="error">
                                            {missing === 1 ? '1 required field is empty' : `${missing} required fields are empty`}
                                        </Typography>
                                    )}
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container direction="row" spacing={2}>
                                    {section.keys.map(renderField)}
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                );
            })}
        </>
    );
};

export default SchemaSections;
